import React, { useCallback, useId } from 'react';
import { AppIcon } from '../../icons/AppIcon';
import Input from '../../ui/Input';
import type { ContactLayoutProps } from './types';
import { useInlineEdit } from './useInlineEdit';

export function ContactLayout({
    icon,
    value,
    placeholder,
    onSave,
    type = 'text',
    formatValue,
}: Omit<ContactLayoutProps, 'layout'>) {
    const fieldId = useId();

    const toDisplayString = useCallback((v: string) => v, []);
    const parse = useCallback((raw: string) => raw, []);

    const onCommit = useCallback(
        (parsed: string) => {
            const finalValue = formatValue ? formatValue(parsed) : parsed;
            if (finalValue !== value) onSave(finalValue);
        },
        [formatValue, value, onSave],
    );

    const edit = useInlineEdit({
        value,
        toDisplayString,
        parse,
        onCommit,
    });

    const handleClear = () => {
        onSave('');
        edit.setLocalValue('');
        edit.endEditing();
    };

    const displayValue = value || placeholder;

    return (
        <div className="flex items-center gap-2 group">
            <AppIcon name={icon} className="w-4 h-4 text-muted flex-shrink-0" />
            {edit.isEditing ? (
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    <Input
                        ref={edit.inputRef}
                        id={fieldId}
                        name={placeholder.toLowerCase().replace(/\s+/g, '-')}
                        type={type}
                        value={edit.localValue}
                        onChange={(e) => edit.setLocalValue(e.target.value)}
                        onBlur={edit.save}
                        onKeyDown={edit.handleKeyDown}
                        placeholder={placeholder}
                        inputSize="sm"
                        variant="filled"
                        containerClassName="flex-1 min-w-0 mb-0"
                        className="h-auto min-h-0 border-transparent shadow-none py-0.5"
                    />
                    {edit.localValue && (
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleClear}
                            className="text-muted hover:text-error transition flex-shrink-0"
                            title="Limpar"
                            aria-label="Limpar campo"
                        >
                            <AppIcon name="x" className="w-3 h-3" />
                        </button>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={edit.startEditing}
                    className="flex-1 text-left text-body-sm text-muted transition hover:text-primary"
                >
                    {displayValue}
                </button>
            )}
        </div>
    );
}
